import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  SquaresFour, FilePlus, Stack, FolderOpen, ClockCounterClockwise,
  ShieldCheck, SignOut, List as ListIcon, X,
} from "@phosphor-icons/react";
import { useState } from "react";

const NAV = [
  { to: "/", label: "Dashboard", icon: SquaresFour, end: true, tid: "nav-dashboard" },
  { to: "/generate", label: "Generate Report", icon: FilePlus, tid: "nav-generate" },
  { to: "/templates", label: "Templates", icon: Stack, tid: "nav-templates" },
  { to: "/repository", label: "Document Repository", icon: FolderOpen, tid: "nav-repository" },
  { to: "/history", label: "Reports History", icon: ClockCounterClockwise, tid: "nav-history" },
  { to: "/audit", label: "Audit Trail", icon: ShieldCheck, tid: "nav-audit" },
];

function Brand() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2.5">
        <img src="/gail-logo.png" alt="GAIL" className="h-9 w-auto shrink-0" data-testid="sidebar-gail-logo" />
        <div>
          <div className="text-white font-bold text-base leading-none tracking-tight">Sankalak <span className="text-[#F59E0B]">||</span> <span className="font-serif-report">संकलक</span></div>
          <div className="text-slate-400 text-[11px] leading-none mt-1">Report Generation</div>
        </div>
      </div>
      <div className="flex justify-center">
        <div className="relative">
          <div className="absolute -inset-2 rounded-full bg-[#F59E0B]/15 blur-xl" />
          <img src="/sanchay-logo.png" alt="Sanchay 2" className="relative h-24 w-auto drop-shadow-lg" data-testid="sidebar-sanchay-logo" />
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const Sidebar = (
    <aside className="w-64 shrink-0 h-full flex flex-col bg-[#0d1530]">
      <div className="p-5"><Brand /></div>
      <div className="tricolor-bar h-1 mx-5 rounded-full" />
      <nav className="flex-1 py-5 px-3 space-y-1.5 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.tid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3.5 py-3 rounded-xl text-[15px] transition-colors ${
                isActive ? "bg-[#F59E0B] text-[#1a1400] font-semibold shadow-lg" : "text-slate-300 hover:bg-white/5"}`}>
            <n.icon size={22} weight={"duotone"} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-10 w-10 rounded-full bg-[#F59E0B] flex items-center justify-center text-[#1a1400] font-bold text-sm shrink-0">
            {(user?.name || "C").split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-100 truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-400 truncate">{user?.designation}</div>
          </div>
        </div>
        <button data-testid="logout-btn" onClick={doLogout}
          className="w-full flex items-center justify-center gap-2 text-sm text-slate-200 bg-white/5 hover:bg-white/10 rounded-xl py-2.5 transition-colors">
          <SignOut size={18} /> Sign Out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="light-shell flex min-h-screen">
      <div className="hidden lg:block h-screen sticky top-0">{Sidebar}</div>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="glass-light sticky top-0 z-30 px-4 sm:px-8 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-700" data-testid="menu-toggle" onClick={() => setOpen(!open)}>
              {open ? <X size={22} /> : <ListIcon size={22} />}
            </button>
            <div>
              <div className="text-sm sm:text-base font-semibold text-slate-900">GAIL (India) Limited</div>
              <div className="text-[11px] text-slate-500">Corporate Strategy, Planning & Advocacy · Executive Dashboard</div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[11px] text-slate-500">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            Secure · Authorized Access
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-8 max-w-[1500px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
